import { USER_ROLE } from "Constants/constant";
import {
  parseOrgAiPayload,
  readOrgAiFromServiceProvider,
} from "helpers/spAiFeatureAccess";
import { parseBooleanVal } from "Utils/utils";

export const AI_ENABLEMENT_REMIND_LATER_KEY = "v360_ai_enablement_remind_later";

/**
 * The SP owner, or a tenant Super Admin manager (full access). Both log in through
 * ServiceProviderResource (userType sp_admin); is_manager_admin marks the manager.
 * Regular managers and workers never see the notice.
 */
export const isAiEnablementNoticeAudience = (user) =>
  Boolean(user) &&
  (user.userType === USER_ROLE.Sp_Admin || parseBooleanVal(user.is_manager_admin) === true);

/**
 * Remind me later lasts for the whole login, whichever provider is selected. It lives in
 * localStorage so refreshes, new tabs, and page-level sessionStorage.clear() calls keep it.
 * Logout (localStorage.clear()) and login (clearAiEnablementRemindLater) remove it.
 */
export const isAiEnablementRemindLaterActive = () => {
  try {
    return localStorage.getItem(AI_ENABLEMENT_REMIND_LATER_KEY) === "1";
  } catch {
    return false;
  }
};

export const remindAiEnablementLater = () => {
  try {
    localStorage.setItem(AI_ENABLEMENT_REMIND_LATER_KEY, "1");
  } catch {
    // ignore quota / private mode
  }
};

export const clearAiEnablementRemindLater = () => {
  try {
    localStorage.removeItem(AI_ENABLEMENT_REMIND_LATER_KEY);
  } catch {
    // ignore
  }
};

/**
 * Organisation flag from login `user.service_provider`, beside `ai_enabled`.
 * Not a role permission.
 */
export const isAiEnablementNoticeDismissed = (user) => {
  const value = user?.service_provider?.ai_enablement_notice_dismissed;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    return ["1", "true", "yes", "on"].includes(value.trim().toLowerCase());
  }
  return false;
};

/** Keep the login user in sync after Don't show again succeeds. */
export const rememberAiEnablementNoticeDismissed = (user) => {
  if (!user || typeof user !== "object") return user;
  const next = {
    ...user,
    service_provider: {
      ...(user.service_provider || {}),
      ai_enablement_notice_dismissed: true,
    },
  };

  try {
    localStorage.setItem("user", JSON.stringify(next));
    const raw = localStorage.getItem("spSettings");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        localStorage.setItem(
          "spSettings",
          JSON.stringify({ ...parsed, ai_enablement_notice_dismissed: true })
        );
      }
    }
  } catch {
    // ignore storage failures
  }

  return next;
};

export const resolveAiEnablementSpId = (user) => {
  try {
    const cached = localStorage.getItem("selectedProvider");
    if (cached) {
      const parsed = JSON.parse(cached);
      const fromProvider = Array.isArray(parsed) ? parsed[0]?.value : parsed?.value;
      if (fromProvider) return fromProvider;
    }
  } catch {
    // fall through to the user record
  }

  return (
    user?.sp_id ||
    user?.service_provider?.id ||
    user?.payment_setting?.sp_id ||
    null
  );
};

export const isOrganisationAiEnabled = (user) => {
  const provider = user?.service_provider;
  if (
    provider &&
    typeof provider === "object" &&
    (provider.ai_enabled !== undefined || provider.ai_org_permissions)
  ) {
    return parseOrgAiPayload(provider).ai_enabled;
  }

  const stored = readOrgAiFromServiceProvider();
  return Boolean(stored?.ai_enabled);
};

/**
 * One-time organisation notice after login.
 * `blocked` covers non-dashboard routes, 2FA, and ghost sessions.
 */
export const shouldShowAiEnablementNotice = ({
  aiFeaturesEnabled = true,
  user,
  spId,
  aiEnabled,
  noticeDismissed = false,
  remindLater = false,
  blocked = false,
} = {}) => {
  if (!aiFeaturesEnabled || blocked) return false;
  if (!user?.id) return false;
  if (!isAiEnablementNoticeAudience(user)) return false;
  if (spId === undefined || spId === null || spId === "") return false;
  if (!aiEnabled) return false;
  if (noticeDismissed) return false;
  if (remindLater) return false;
  return true;
};
